import React, { useEffect, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useGetCoursesQuery, useUploadToS3Mutation, useUploadCourseCsvMutation } from "../../store/services/university-service";
import Table from "../../components/Table/Table";
import { Alert, Autocomplete, Button, IconButton, Modal, Snackbar, TextField, Chip } from "@mui/material";
import Box from "@mui/material/Box";
import AddIcon from "@mui/icons-material/Add";
import SharingDisplay from '../../components/ContentSharing/SharingDisplay';
import { useNavigate } from "react-router-dom";
import UsersTable from "../users/UsersTable";
import AddCourseModal from "../on-demand/CreateOnDemand/AddCourseModal";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CoursePreview from "./CoursePreview";
import CourseEdit from "./CourseEdit";
import { useTheme } from "@mui/material/styles";

const userModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    boxShadow: 24,
    borderRadius: 4,
    bgcolor: "background.paper"
};

CourseTable.propTypes = {
    initialData: PropTypes.object,
    type: PropTypes.string.isRequired,
    courseIds: PropTypes.array,
    setCourseId: PropTypes.func,
    onRandomSelect: PropTypes.func
};

export default function CourseTable(props) {
    const { initialData, type, courseIds = [], setCourseId, onRandomSelect } = props;
    const navigate = useNavigate();
    const theme = useTheme();
    const { tenancyId, featureSwitches: actualData, featureSwitchesLoading } = useSelector((state) => state.tenancy);
    const additionalQueryParams = type === "edit" ? "" : `&published=true&tenancyId=${tenancyId}`;
    const [isAdmin, setIsAdmin] = useState(false);

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 100,
        page: 0
    });

    const { data: coursesData, isLoading: coursesLoading, error: coursesError } = useGetCoursesQuery({
        perPage: paginationModel.pageSize,
        page: paginationModel.page,
        includes: "",
        query: "",
        additionalQueryParams: additionalQueryParams
    });

    const [uploadCourseCsv] = useUploadCourseCsvMutation();

    const [courseTypes, setCourseTypes] = useState([]);
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const fileInputRef = useRef(null);
    const [userModalOpen, setUserModalOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [addCourseModalOpen, setAddCourseModalOpen] = useState(false);

    // State for tenancy filter (library mode only)
    const [tenancyFilter, setTenancyFilter] = useState("My Courses");

    const tenancyFilterOptions = [
        "My Courses",
        "Other Courses",
        "All Courses"
    ];

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        setIsAdmin(user && user.role === "Administrators");
    }, []);

    useEffect(() => {
        if (coursesData) {
            const allLanguages = [];
            coursesData.forEach((course) => {
                // Handle both old and new data structures
                if (course.languageCodes && Array.isArray(course.languageCodes)) {
                    allLanguages.push(...course.languageCodes);
                } else if (course.languageCode) {
                    allLanguages.push(course.languageCode);
                }
            });
            const uniqueTypes = [...new Set(allLanguages)];
            setCourseTypes(uniqueTypes);
        }
    }, [coursesData]);

    // Filtering function - replaces the old filteredCourses state logic
    const getFilteredCourses = () => {
        if (!coursesData) {
            return [];
        }

        let filtered = coursesData;

        // Apply language filter
        if (selectedLanguages && selectedLanguages.length > 0) {
            filtered = filtered.filter((course) => {
                const courseLanguages = course.languageCodes && Array.isArray(course.languageCodes)
                    ? course.languageCodes
                    : [course.languageCode];
                return courseLanguages.some(lang => selectedLanguages.includes(lang));
            });
        }

        // Apply tenancy filter for library mode
        if (type === "library") {
            if (tenancyFilter === "My Courses") {
                filtered = filtered.filter(course =>
                    course.sharedTenancyIds && course.sharedTenancyIds.includes(Number(tenancyId))
                );
            } else if (tenancyFilter === "Other Courses") {
                filtered = filtered.filter(course =>
                    !course.sharedTenancyIds || !course.sharedTenancyIds.includes(Number(tenancyId))
                );
            }
            // "All Courses" doesn't need additional filtering
        }

        return filtered;
    };

    const handleCourseTypeChange = (event, value) => {
        setSelectedLanguages(value || []);
    };

    const handleTenancyFilterChange = (event, value) => {
        setTenancyFilter(value || "My Courses");
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            handleSendCourseUsingCSV(file);
        }
    };

    const handleSendCourseUsingCSV = async (file) => {
        if (file.type === "application/vnd.ms-excel" || file.type === "text/csv") {
            try {
                const response = await uploadCourseCsv({ file }).unwrap();
                setSnackbarMessage(response.success ?
                    "Upload successful" :
                    "Error uploading CSV");
                setSnackbarSeverity(response.success ? "success" : "error");
                setSnackbarOpen(true);
            } catch (error) {
                console.error("Error uploading CSV:", error);
                setSnackbarMessage(error.data || "Error uploading CSV");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            }
        } else {
            setSnackbarMessage("Invalid file type. Please upload a CSV file.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    };

    const handleAddCourse = () => {
        if (type === "edit") {
            navigate("/courses/add");
        }
        if (type === "library") {
            navigate("/tenancy-courses/add");
            
        } else {
            setAddCourseModalOpen(true);
        }
    };

    const handleViewCourse = (course) => {
        navigate(`/courses/${course.internalId}`);
    };

    const handleSelectCourse = (course) => {
        setCourseId(prevSelected => {
            const isAlreadySelected = prevSelected.some(c => c.id === course.id);
            if (isAlreadySelected) {
                return prevSelected.filter(c => c.id !== course.id);
            } else {
                return [...prevSelected, course];
            }
        });
    };

    const handlePreviewCourse = (course) => {
        setSelectedCourseId(course.id);
        setPreviewModalOpen(true);
    };

    const handleClosePreviewModal = () => {
        setPreviewModalOpen(false);
        setSelectedCourseId(null);
    };

    const handleOpenEditModal = (course) => {
        if (type === "library") {
            navigate(`/tenancy-courses/edit/${course.internalId}`);
        } else {
            setSelectedCourseId(course.internalId);
            setEditModalOpen(true);
        }
    };

    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setSelectedCourseId(null);
    };

    const handleSaveCourse = async (savedCourse) => {
        handleCloseEditModal();
    };

    const handleBrowseAllCourses = () => {
        setTenancyFilter("All Courses");
    };

    // Check if we should show library empty state
    const showLibraryEmptyState = type === "library" &&
        tenancyFilter === "My Courses" &&
        getFilteredCourses().length === 0 &&
        !coursesLoading &&
        !coursesError;

    const headCells = [
        {
            id: "select",
            label: "Select",
            render: (dataRow) => {
                const isSelected = Array.isArray(courseIds) && courseIds.some(course => course.id === dataRow.id);
                return (
                    <IconButton
                        size="small"
                        onClick={() => {
                            if (isSelected) {
                                setCourseId(courseIds.filter(course => course.id !== dataRow.id));
                            } else {
                                setCourseId([...courseIds, dataRow]);
                            }
                        }}
                    >
                        {isSelected ? <CheckBoxRoundedIcon color="warning" /> : <CheckBoxOutlineBlankRoundedIcon />}
                    </IconButton>
                );
            },
            initialVisible: type === "select"
        },
        {
            id: "preview",
            label: "Preview",
            render: (dataRow) => (
                <Button
                    size="small"
                    onClick={() => handlePreviewCourse(dataRow)}
                    aria-label="preview course"
                    variant="contained"
                    color="warning"
                >Preview
                </Button>
            ),
            initialVisible: true
        },
        {
            id: "edit",
            label: "Edit",
            render: (dataRow) => {
                // Updated edit visibility logic for library mode
                let isVisible = false;
                if (type === "edit") {
                    // Edit mode - show edit button
                    isVisible = true;
                } else if (type === "library") {
                    // Library mode: only show edit for user's own tenancy courses
                    isVisible = isAdmin || (dataRow.sharedTenancyIds && dataRow.sharedTenancyIds.includes(Number(tenancyId)));
                }

                return (
                    <Button
                        size="small"
                        onClick={() => handleOpenEditModal(dataRow)}
                        aria-label="edit course"
                        variant="contained"
                        color="secondary"
                        sx={{ display: isVisible ? "block" : "none" }}
                    >Edit
                    </Button>
                );
            },
            initialVisible: type === "edit" || type === "library"
        },
        {
            id: "send-user",
            label: type === "select" ? "Send User" : " ",
            render: (dataRow) => (
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => handleOpenUserModal(dataRow.id)}
                >
                    send to user
                </Button>
            ),
            initialVisible: type === "edit"
        },
        {
            id: "courseName",
            label: "Course Name",
            initialVisible: true
        },
        {
            id: "languageCode",
            label: "Languages",
            render: (dataRow) => {
                // Handle both new array format and old single format
                const languages = dataRow.languageCodes && Array.isArray(dataRow.languageCodes)
                    ? dataRow.languageCodes
                    : (dataRow.languageCode ? [dataRow.languageCode] : []);

                return languages.join(", ");
            },
            initialVisible: true
        },
        {
            id: "sharing",
            label: "Sharing",
            render: (dataRow) => (
                <SharingDisplay
                    published={dataRow.published}
                    sharedTenancyIds={dataRow.sharedTenancyIds}
                    publishToFamily={dataRow.publishToFamily}
                    variant="compact"
                />
            ),
            initialVisible: true
        },
        {
            id: 'format',
            label: 'Format',
            initialVisible: true
        }
    ];

    const toolbarActions = [{
        render: () => (
            <Box width={"100%"} justifyContent={"flex-end"} display={"flex"} alignItems={"center"}>
                {/* Tenancy Filter for Library Mode */}
                {type === "library" && (
                    <Autocomplete
                        id="tenancy-filter-autocomplete"
                        disableClearable
                        sx={{ mr: 2, width: 180 }}
                        size="small"
                        value={tenancyFilter}
                        options={tenancyFilterOptions}
                        onChange={handleTenancyFilterChange}
                        renderInput={(params) =>
                            <TextField {...params}
                                style={{ backgroundColor: 'background.default' }}
                                label="Sharing"
                            />
                        }
                    />
                )}

                <Autocomplete
                    id={"language-filter"}
                    multiple
                    sx={{ mr: 2, minWidth: 180 }}
                    size="small"
                    value={selectedLanguages || []}
                    options={courseTypes}
                    onChange={handleCourseTypeChange}
                    renderInput={(params) => <TextField {...params} label="Languages" />}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                variant="outlined"
                                label={option}
                                size="small"
                                {...getTagProps({ index })}
                                key={option}
                            />
                        ))
                    }
                />
                {(type === 'edit' || type === 'library') && (actualData?.enableAddCourse) && (
                    <Button
                        size="medium"
                        variant="contained"
                        sx={{ textTransform: "capitalize" }}
                        onClick={handleAddCourse}
                        endIcon={<AddIcon />}
                    >
                        Add Course
                    </Button>
                )}
                {type !== "select" && type !== "library" && isAdmin && (
                    <>
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                            onChange={handleFileChange}
                        />
                        <Button
                            size="medium"
                            variant="contained"
                            color="secondary"
                            sx={{ bgcolor: (theme) => theme.palette.secondary.lighter, ml: 2, }}
                            onClick={() => fileInputRef.current.click()}
                        >
                            send using csv
                        </Button>
                    </>
                )}
            </Box>
        )
    }];

    const handleCloseUserModal = () => {
        setUserModalOpen(false);
    };

    const handleOpenUserModal = (simulationId) => {
        setSelectedUserId(simulationId);
        setUserModalOpen(true);
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    return (
        <>
            <Table
                name={"Courses"}
                data={getFilteredCourses()}
                error={coursesError}
                loading={coursesLoading}
                headCells={headCells}
                search={true}
                editColumn={false}
                checkbox={false}
                showDelete={false}
                toolbarActions={toolbarActions}
                showToolbarActions={true}
                onDelete={() => {}}
                onCellClick={handleViewCourse}
                selected={courseIds}
                setSelected={handleSelectCourse}
                maxTableHeight={type === "select" ? "calc(100vh - 350px)" : "calc(100vh - 250px)"}
                initialVisibleColumns={headCells.filter(cell => cell.initialVisible).map(cell => cell.label)}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                keepSearchInSelectMode={type === "select"}
                emptyStateConfig={showLibraryEmptyState ? {
                    title: "No custom courses found for your organisation",
                    message: actualData?.enableAddCourse
                        ? "Change the sharing filter to 'All Courses' to browse our full library, or click 'Add Course' to create a custom course."
                        : "Change the sharing filter to 'All Courses' to browse our full library.",
                    actions: [
                        {
                            label: "Browse All Courses",
                            onClick: handleBrowseAllCourses,
                            variant: "outlined"
                        },
                        ...(actualData?.enableAddCourse ? [{
                            label: "Add Course",
                            onClick: handleAddCourse,
                            variant: "contained",
                            icon: <AddIcon />
                        }] : [])
                    ]
                } : undefined}
            />

            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            <Modal
                open={userModalOpen}
                onClose={handleCloseUserModal}
                aria-labelledby="user-selection-modal"
                aria-describedby="user-selection-description"
            >
                <Box sx={userModalStyle}>
                    <UsersTable
                        type={"send"}
                        userId={selectedUserId}
                        setUserId={setSelectedUserId}
                        formatId={selectedUserId}
                        format={"course"}
                        CSVButton
                    />
                </Box>
            </Modal>

            <Modal
                open={previewModalOpen}
                onClose={handleClosePreviewModal}
                aria-labelledby="course-preview-modal"
                aria-describedby="course-preview-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    borderRadius: '8px',
                    width: '50%',
                    height: '90vh',
                    p: 2,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    maxHeight: '90vh',
                    overflow: 'auto',
                }}>
                    {selectedCourseId && <CoursePreview courseId={selectedCourseId} />}
                </Box>

            </Modal>

            <Modal
                open={editModalOpen}
                onClose={handleCloseEditModal}
                aria-labelledby="course-edit-modal"
                aria-describedby="course-edit-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '50%',
                    borderRadius: '8px',
                    bgcolor: 'background.default',
                    boxShadow: 24,
                    p: 4,
                    maxHeight: '90vh',
                    overflow: 'auto',
                }}>
                    {selectedCourseId && (
                        <CourseEdit
                            courseId={selectedCourseId}
                            onSave={handleSaveCourse}
                            onClose={handleCloseEditModal}
                        />
                    )}
                </Box>
            </Modal>

            <AddCourseModal open={addCourseModalOpen} handleClose={() => setAddCourseModalOpen(false)} />
        </>
    );
}